import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { collection, doc, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { useTranslation } from "../hooks/useTranslation";
import { translatePreservingFrench } from "../utils/lang";

const ContentContext = createContext();

export const ContentProvider = ({ children }) => {
  const [originalCategories, setOriginalCategories] = useState([]);
  const [originalSeries, setOriginalSeries] = useState([]);
  const [originalLiveEpisodes, setOriginalLiveEpisodes] = useState([]);

  const [categories, setCategories] = useState([]);
  const [series, setSeries] = useState([]);
  const [liveEpisodes, setLiveEpisodes] = useState([]);

  const [loading, setLoading] = useState(true);
  const [translating, setTranslating] = useState(false);
  const { currentLanguage, translateText } = useTranslation();

  const [translationCache, setTranslationCache] = useState(new Map());

  useEffect(() => {
    try {
      const raw = localStorage.getItem("translationCache_v1");
      if (raw) {
        const obj = JSON.parse(raw);
        if (obj && typeof obj === "object") {
          setTranslationCache(new Map(Object.entries(obj)));
        }
      }
    } catch (e) {
      console.warn("Failed to read translation cache:", e);
    }
  }, []);

  const TRANSLATION_CACHE_VERSION = "1";

  const getCachedTranslation = useCallback(
    async (text, targetLang) => {
      if (!text || targetLang === "en") return text;
      const normalizedTarget = (targetLang || "").toLowerCase();
      const cacheKey = `${TRANSLATION_CACHE_VERSION}|${normalizedTarget}|${text}`;
      if (translationCache.has(cacheKey)) {
        return translationCache.get(cacheKey);
      }
      try {
        const output =
          normalizedTarget === "fr"
            ? await translatePreservingFrench(text, "fr", translateText)
            : await translateText(text, normalizedTarget);

        setTranslationCache((prev) => {
          const next = new Map(prev);
          next.set(cacheKey, output);
          try {
            const obj = Object.fromEntries(next);
            localStorage.setItem("translationCache_v1", JSON.stringify(obj));
          } catch (e) {
            console.warn("Failed to persist translation cache:", e);
          }
          return next;
        });

        return output;
      } catch (e) {
        console.error("Translation error:", e);
        return text;
      }
    },
    [translateText, translationCache],
  );

  useEffect(() => {
    // Categories listener
    const unsubscribeCategories = onSnapshot(
      doc(db, "content", "main"),
      (docSnap) => {
        setOriginalCategories(docSnap.data()?.categories || []);
      },
    );

    // Series collection listener
    const seriesRef = collection(db, "content", "main", "series");
    const unsubscribeSeries = onSnapshot(seriesRef, (seriesSnapshot) => {
      const seriesData = seriesSnapshot.docs.map((seriesDoc) => ({
        id: seriesDoc.id,
        ...seriesDoc.data(),
      }));

      setOriginalSeries(seriesData);
      setLoading(false);
    });

    // Live episodes listener
    const liveEpisodesQuery = query(
      collection(db, "episodes"),
      where("isLive", "==", true),
    );

    const unsubscribeLiveEpisodes = onSnapshot(
      liveEpisodesQuery,
      (snapshot) => {
        const liveEpisodesData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setOriginalLiveEpisodes(liveEpisodesData);
      },
    );

    return () => {
      unsubscribeCategories();
      unsubscribeSeries();
      unsubscribeLiveEpisodes();
    };
  }, []);

  const translateCategories = useCallback(
    async (list) => {
      if (currentLanguage === "en") return list;
      return Promise.all(
        (list || []).map(async (category) => ({
          ...category,
          name: await getCachedTranslation(category.name, currentLanguage),
          description: category.description
            ? await getCachedTranslation(category.description, currentLanguage)
            : category.description,
        })),
      );
    },
    [currentLanguage, getCachedTranslation],
  );

  const translateSeries = useCallback(
    async (list) => {
      if (currentLanguage === "en") return list;
      return Promise.all(
        (list || []).map(async (item) => ({
          ...item,
          title: await getCachedTranslation(item.title, currentLanguage),
          description: item.description
            ? await getCachedTranslation(item.description, currentLanguage)
            : item.description,
          episodes: item.episodes
            ? await Promise.all(
                item.episodes.map(async (episode) => ({
                  ...episode,
                  title: await getCachedTranslation(
                    episode.title,
                    currentLanguage,
                  ),
                  description: episode.description
                    ? await getCachedTranslation(
                        episode.description,
                        currentLanguage,
                      )
                    : episode.description,
                })),
              )
            : item.episodes,
        })),
      );
    },
    [currentLanguage, getCachedTranslation],
  );

  const translateLiveEpisodes = useCallback(
    async (list) => {
      if (currentLanguage === "en") return list;
      return Promise.all(
        (list || []).map(async (episode) => ({
          ...episode,
          title: await getCachedTranslation(episode.title, currentLanguage),
          description: episode.description
            ? await getCachedTranslation(episode.description, currentLanguage)
            : episode.description,
        })),
      );
    },
    [currentLanguage, getCachedTranslation],
  );

  useEffect(() => {
    const doTranslate = async () => {
      if (loading) return;
      setTranslating(true);
      try {
        const [translatedCategories, translatedSeries, translatedLiveEpisodes] =
          await Promise.all([
            translateCategories(originalCategories),
            translateSeries(originalSeries),
            translateLiveEpisodes(originalLiveEpisodes),
          ]);

        setCategories(translatedCategories);
        setSeries(translatedSeries);
        setLiveEpisodes(translatedLiveEpisodes);
      } catch (e) {
        console.error("Translation pipeline error:", e);
        setCategories(originalCategories);
        setSeries(originalSeries);
        setLiveEpisodes(originalLiveEpisodes);
      } finally {
        setTranslating(false);
      }
    };

    doTranslate();
  }, [
    currentLanguage,
    originalCategories,
    originalSeries,
    originalLiveEpisodes,
    loading,
    translateCategories,
    translateSeries,
    translateLiveEpisodes,
  ]);

  const contextValue = {
    categories,
    series,
    liveEpisodes,
    loading: loading || translating,
    isTranslating: translating,
    currentLanguage,
    originalData: {
      categories: originalCategories,
      series: originalSeries,
      liveEpisodes: originalLiveEpisodes,
    },
  };

  return (
    <ContentContext.Provider value={contextValue}>
      {children}
    </ContentContext.Provider>
  );
};

export const useContent = () => useContext(ContentContext);
